/*
 * @author BSG <dev@bsgroup.eu>
 * @copyright Better Software Group S.A.
 * @version: 1.0
 */

import { ImageHelper } from "@nf/common";
import { RouteHelper } from "helpers/routeHelper";
import { TILE_OUT_OF_VIEW_WIDTH } from "@nf/constants";
import React, { useEffect, useRef, useState } from "react";
import dynamic from "next/dynamic";
import cx from "classnames";

import AvailabilityLabel from "components/AvailabilityLabel";
import ImageWithPlaceholder from "components/ImageWithPlaceholder";
import LinkWithLocale from "components/LinkWithLocale";
import { getItemBounds } from "../../../../utils";
import { type IListComponentItemProps } from "../../models";

import styles from "./ListComponentItemCover.module.scss";
import { isServerSideRender } from "@nf/helpers";

const ListComponentItemModal = dynamic(
  () => import("../ListComponentItemModal")
);

export type IListComponentItemCoverProps = IListComponentItemProps;

export const ListComponentItemCover = ({
  media,
  query,
  cellType,
  isPlaceholder = false,
}: IListComponentItemCoverProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const timeoutRef = useRef<number | null>(null);
  const imageUrl = ImageHelper.getPosterImageUrl(media?.Images) ?? "";

  const [shouldRender, setShouldRender] = useState(false);
  const [isOutOfView, setIsOutOfView] = useState(false);
  const [isHovered, setIsHovered] = useState(false);

  const isDesktopSize = !isServerSideRender && window.innerWidth > 850;

  const itemBounds = getItemBounds(ref.current);

  const handleModalAnimationEnd = () => {
    setShouldRender(false);

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  };

  const handleMouseLeave = () => {
    setIsHovered(false);

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  };

  const handleMouseEnter = () => {
    timeoutRef.current = window.setTimeout(() => {
      setShouldRender(true);
      setIsHovered(true);

      if (!itemBounds) return;

      const isTileOutOnTheLeft = itemBounds.left < 0;
      const isTileOutOnTheRight =
        itemBounds.right > window.innerWidth + TILE_OUT_OF_VIEW_WIDTH;

      if (isTileOutOnTheLeft || isTileOutOnTheRight) {
        setIsOutOfView(true);
      } else {
        setIsOutOfView(false);
      }
    }, 200);
  };

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  if (!media || isPlaceholder) {
    return <div className={cx(styles.item, styles.skeleton)} />;
  }

  return (
    <div
      ref={ref}
      className={cx(styles.item, isOutOfView && styles.isOutOfView)}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <AvailabilityLabel media={media} />

      <LinkWithLocale href={RouteHelper.goToDetails(media, query)}>
        <a aria-label={media.Title} className={styles.link}>
          <ImageWithPlaceholder
            alt={media.Title}
            src={imageUrl}
            className={styles.image}
          />
        </a>
      </LinkWithLocale>

      {itemBounds && shouldRender && !isOutOfView && isDesktopSize && (
        <ListComponentItemModal
          media={media}
          query={query}
          cellType={cellType}
          open={isHovered}
          bounds={itemBounds}
          onAnimationEnd={handleModalAnimationEnd}
        />
      )}
    </div>
  );
};
